import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import type { ReactNode } from "react";

export interface SidebarProps {
  /**
   * Navigation content — typically section headers + nav items. Maps to
   * v35's `.sidebar-nav`. Composition (which sections/items) is the
   * surface's concern; this primitive owns the rail chrome only.
   */
  children: ReactNode;
  /** Accessible name for the `<nav>` landmark. Default: "Primary". */
  ariaLabel?: string;
  /** Optional extra classes for the rail. */
  className?: string;
  /**
   * Collapsed (folded) state. When true the rail narrows to an icon-only
   * stub and the nav content is hidden — the surface owns the state +
   * persistence; this primitive only renders the two visual modes.
   */
  collapsed?: boolean;
  /** Toggle the collapsed state. When provided, a fold/unfold control renders. */
  onToggleCollapsed?: () => void;
}

/**
 * Sidebar — surface-agnostic left navigation rail.
 *
 * v35 `.sidebar`: fixed 260px wide, surface bg, 1px right border,
 * vertically scrollable, never shrinks. Renders a `<nav>` landmark so
 * assistive tech can jump to it. Content (section headers, nav items,
 * dividers) is composed by the surface.
 *
 * Tokenized: `w-64` (256px ≈ v35's 260px on the 4px scale — the 4px
 * delta is below perceptual threshold and keeps us on the token scale,
 * avoiding an arbitrary `w-[260px]`), `bg-surface`, `border-border`.
 */
export function Sidebar({
  children,
  ariaLabel = "Primary",
  className,
  collapsed = false,
  onToggleCollapsed,
}: SidebarProps) {
  // Collapsed → a thin icon rail (w-12); expanded → the full w-64 rail.
  const width = collapsed ? "w-12" : "w-64";
  const rail = `${width} shrink-0 overflow-y-auto bg-surface border-r border-border`;

  return (
    <nav
      aria-label={ariaLabel}
      data-testid="app-sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      className={[rail, className].filter(Boolean).join(" ")}
    >
      <div className="flex flex-col py-3">
        {onToggleCollapsed ? (
          <div className={collapsed ? "flex justify-center px-2 pb-2" : "flex justify-end px-3 pb-2"}>
            <button
              type="button"
              onClick={onToggleCollapsed}
              data-testid="sidebar-toggle"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-expanded={!collapsed}
              className="inline-flex items-center justify-center rounded-md p-1.5 text-text-secondary hover:bg-accent-muted hover:text-accent"
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
              ) : (
                <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>
        ) : null}
        {collapsed ? null : children}
      </div>
    </nav>
  );
}
